import { useMutation, useQuery } from '@tanstack/react-query';

import { toast } from 'sonner';
import { useUser } from '@/providers/user-provider';
import {
  changePassword,
  fetchSession,
  forgotPassword,
  resendEmailOtp,
  resetPassword,
  signIn,
  signInWithSocial,
  signUp,
  updateUser,
  verifyEmail,
} from '.';

export const useSignIn = () => {
  const { setSession } = useUser();

  return useMutation({
    mutationFn: signIn,
    onSuccess: async (data) => {
      setSession(data);
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useSignUp = () => {
  const { setSession } = useUser();

  return useMutation({
    mutationFn: signUp,
    onSuccess: (data) => {
      setSession(data);
      toast.success('Account Created', {
        description:
          'Account created successfully! Please check your email to verify your account.',
      });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: forgotPassword,
    onSuccess: () => {
      toast.success('Code Sent', {
        description: "We've sent a verification code to your email",
      });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useVerifyEmail = () => {
  const user = useUser();

  return useMutation({
    mutationFn: verifyEmail,
    onSuccess: async () => {
      user.verifyEmail();

      toast.success('Email Verified', {
        description: 'Your email has been successfully verified',
      });
    },
    onError: async (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      toast.success('Password Reset', {
        description: 'Your password has been successfully reset',
      });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      toast.success('Password Changed', {
        description: 'Your password has been successfully changed',
      });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useResendEmailOtp = () => {
  return useMutation({
    mutationFn: resendEmailOtp,
    onSuccess: () => {
      toast.success('Code Sent', {
        description: "We've sent a new verification code to your email",
      });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useSession = () => {
  return useQuery({
    queryKey: ['session'],
    queryFn: fetchSession,
  });
};

export const useSocialSignIn = () => {
  return useMutation({
    mutationFn: signInWithSocial,
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useUpdateUser = () => {
  const { updateUser: updateUserContext } = useUser();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (_, variables) => {
      updateUserContext(variables);
      toast.success('Profile Updated', {
        description: 'Your profile has been successfully updated',
      });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};
